/**
 * Dataset Detail Page
 *
 * Shows dataset overview with tabs for files and annotations.
 */

import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Tab,
  Tabs,
} from '@heroui/react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Database, Download, FileAudio, Tag, Trash2, Upload } from 'lucide-react';

import { useDataset } from '@/lib/api/hooks/datasets';
import { useAudioFiles, useDeleteAudioFile, useGetDownloadUrlMutation } from '@/lib/api/hooks/audio';
import { DataTable, type Column } from '@/components/data/DataTable';
import { AnnotationSetList, AnnotationEditor } from '@/components/features/annotations';
import type { AudioFileSummary } from '@/api';
import { formatRelativeTime, formatBytes, formatDuration } from '@/lib/design-system';

interface DatasetDetailSearch {
  tab?: 'overview' | 'files' | 'annotations';
}

export const Route = createFileRoute('/datasets/$datasetId/')({
  validateSearch: (search: Record<string, unknown>): DatasetDetailSearch => {
    return {
      tab: (search.tab as DatasetDetailSearch['tab']) || 'overview',
    };
  },
  component: DatasetDetailPage,
});

function DatasetDetailPage() {
  const { datasetId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { data: dataset, isLoading, error } = useDataset(datasetId);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div className="container-page py-6">
        <Card>
          <CardBody className="text-center text-danger">
            Failed to load dataset. It may have been deleted.
          </CardBody>
        </Card>
      </div>
    );
  }

  const artifactTypes = (dataset.artifact_types ?? []).map((type) => ({
    name: type.name as string,
    color: (type.color as string) || '#6366f1',
  }));

  return (
    <div className="container-page py-6 animate-fade-in">
      <div className="mb-6">
        <Button
          variant="light"
          size="sm"
          startContent={<ArrowLeft className="h-4 w-4" />}
          onPress={() => navigate({ to: '/datasets' })}
          className="mb-4"
        >
          Back to Datasets
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1>{dataset.name}</h1>
            {dataset.description && (
              <p className="mt-1 text-default-500">{dataset.description}</p>
            )}
            <p className="mt-2 text-sm text-default-400">
              Created {formatRelativeTime(new Date(dataset.created_at))}
            </p>
          </div>
          <Button
            as={Link}
            to={`/datasets/${datasetId}/upload`}
            color="primary"
            startContent={<Upload className="h-4 w-4" />}
          >
            Upload Audio
          </Button>
        </div>
      </div>

      <Tabs
        selectedKey={tab}
        onSelectionChange={(key) => {
          navigate({
            to: `/datasets/${datasetId}`,
            search: { tab: key as DatasetDetailSearch['tab'] },
          });
        }}
        classNames={{
          tabList: 'border-b border-default-200',
        }}
      >
        <Tab
          key="overview"
          title={
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Overview
            </div>
          }
        >
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <h3 className="font-semibold">Statistics</h3>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-default-500">Audio Files</span>
                  <Chip size="sm" variant="flat" classNames={{ content: 'tabular-nums' }}>
                    {dataset.audio_count ?? 0}
                  </Chip>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-default-500">Annotation Versions</span>
                  <Chip size="sm" variant="flat" color="secondary" classNames={{ content: 'tabular-nums' }}>
                    {dataset.annotation_set_count ?? 0}
                  </Chip>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Artifact Types</h3>
              </CardHeader>
              <CardBody>
                {artifactTypes.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {artifactTypes.map((type, index) => (
                      <Chip
                        key={index}
                        size="sm"
                        variant="flat"
                        style={{
                          backgroundColor: `${type.color}20`,
                          color: type.color,
                        }}
                      >
                        {type.name}
                      </Chip>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-default-500">No artifact types defined</p>
                )}
              </CardBody>
            </Card>
          </div>
        </Tab>

        <Tab
          key="files"
          title={
            <div className="flex items-center gap-2">
              <FileAudio className="h-4 w-4" />
              Files
              <Chip size="sm" variant="flat" classNames={{ content: 'tabular-nums' }}>
                {dataset.audio_count ?? 0}
              </Chip>
            </div>
          }
        >
          <div className="mt-6">
            <FilesTab datasetId={datasetId} />
          </div>
        </Tab>

        <Tab
          key="annotations"
          title={
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Annotations
              <Chip size="sm" variant="flat" color="secondary" classNames={{ content: 'tabular-nums' }}>
                {dataset.annotation_set_count ?? 0}
              </Chip>
            </div>
          }
        >
          <div className="mt-6">
            <AnnotationsTab datasetId={datasetId} artifactTypes={artifactTypes} />
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

function FilesTab({ datasetId }: { datasetId: string }) {
  const { data: audioFiles, isLoading } = useAudioFiles(datasetId);
  const deleteAudioFile = useDeleteAudioFile(datasetId);
  const getDownloadUrl = useGetDownloadUrlMutation(datasetId);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<AudioFileSummary | null>(null);

  const handleDownload = async (audioId: string, filename: string) => {
    try {
      const result = await getDownloadUrl.mutateAsync(audioId);
      if (result?.download_url) {
        const link = document.createElement('a');
        link.href = result.download_url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch {
      // Error handling is managed by the mutation
    }
  };

  const handleDeleteClick = (file: AudioFileSummary) => {
    setFileToDelete(file);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (fileToDelete) {
      try {
        await deleteAudioFile.mutateAsync(fileToDelete.id);
        setDeleteModalOpen(false);
        setFileToDelete(null);
      } catch {
        // Error handling is managed by the mutation
      }
    }
  };

  const getStatusColor = (status: AudioFileSummary['processing_status']) => {
    switch (status) {
      case 'ready':
        return 'success';
      case 'failed':
        return 'danger';
      case 'pending':
      case 'normalizing':
      case 'embedding':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns: Column<AudioFileSummary>[] = [
    {
      key: 'original_filename',
      label: 'Filename',
      sortable: true,
    },
    {
      key: 'duration_ms',
      label: 'Duration',
      align: 'end',
      hideOnMobile: true,
      render: (row) =>
        row.duration_ms ? formatDuration(row.duration_ms) : '-',
    },
    {
      key: 'file_size_bytes',
      label: 'Size',
      align: 'end',
      hideOnMobile: true,
      render: (row) => formatBytes(row.file_size_bytes),
    },
    {
      key: 'processing_status',
      label: 'Status',
      render: (row) => (
        <Chip
          size="sm"
          variant="flat"
          color={getStatusColor(row.processing_status)}
        >
          {row.processing_status}
        </Chip>
      ),
    },
    {
      key: 'created_at',
      label: 'Uploaded',
      hideOnMobile: true,
      render: (row) => formatRelativeTime(new Date(row.created_at)),
    },
    {
      key: 'actions',
      label: '',
      align: 'end',
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="light"
            isIconOnly
            isDisabled={row.processing_status !== 'ready' || getDownloadUrl.isPending}
            onPress={() => handleDownload(row.id, row.original_filename)}
            aria-label={`Download ${row.original_filename}`}
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="light"
            color="danger"
            isIconOnly
            isDisabled={deleteAudioFile.isPending}
            onPress={() => handleDeleteClick(row)}
            aria-label={`Delete ${row.original_filename}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const files = audioFiles?.items ?? [];

  return (
    <>
      <DataTable
        columns={columns}
        data={files}
        isLoading={isLoading}
        emptyMessage="No audio files"
        emptyDescription="Upload audio files to get started with training."
        emptyAction={
          <Button
            as={Link}
            to={`/datasets/${datasetId}/upload`}
            color="primary"
            startContent={<Upload className="h-4 w-4" />}
          >
            Upload Audio
          </Button>
        }
        pagination
        rowsPerPage={10}
      />

      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Delete Audio File</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to delete{' '}
              <span className="font-medium">{fileToDelete?.original_filename}</span>?
            </p>
            <p className="mt-2 text-sm text-default-500">
              This action cannot be undone. All associated embeddings will also be deleted.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleDeleteConfirm}
              isLoading={deleteAudioFile.isPending}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

interface ArtifactType {
  name: string;
  color: string;
}

function AnnotationsTab({
  datasetId,
  artifactTypes,
}: {
  datasetId: string;
  artifactTypes: ArtifactType[];
}) {
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  if (artifactTypes.length === 0) {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <p className="text-default-500">
            No artifact types defined for this dataset. Add artifact types in the dataset settings before creating annotations.
          </p>
        </CardBody>
      </Card>
    );
  }

  if (selectedSetId) {
    return (
      <AnnotationEditor
        datasetId={datasetId}
        annotationSetId={selectedSetId}
        artifactTypes={artifactTypes}
        onBack={() => setSelectedSetId(null)}
      />
    );
  }

  return (
    <AnnotationSetList
      datasetId={datasetId}
      onSelectSet={setSelectedSetId}
    />
  );
}
